/**
 * Receivables page (`/receivables`).
 *
 * Three manager-pending workflow surfaces, each grounded in the same
 * "what's the client sitting on" question. Patch E21.
 *
 *   1. BG Awaiting Acceptance — submitted to the client, not yet signed off
 *   2. Awaiting Payment — invoice submitted (stage 16), cheque not cleared
 *      (stage 18). Sub-split: cheque released (stage 17) but not cleared
 *      vs. cheque not yet released.
 *   3. Earnest Awaiting Release — earnest money submitted, not yet returned
 *
 * Plus an aggregate BG-exposure stat tile so the manager has an instant
 * capital-locked-up readout.
 *
 * Each list sorts most-overdue first (largest daysSinceX), matching the
 * manager's natural triage order.
 */
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Banknote,
  Coins,
  MessageCircleReply,
  ReceiptText,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { formatCurrency, formatDate, formatRelative } from '@procurement/shared';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ExportCsvButton } from '@/components/ui/export-csv-button';
import { Input } from '@/components/ui/form-field';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Spinner } from '@/components/ui/spinner';
import { StatTile } from '@/components/ui/stat-tile';
import { LogCommunicationModal } from '@/components/attachments/log-communication-modal';
import { useReceivables, type ReceivablesResponse } from '@/lib/visibility-queries';
import { useLastContact, type LastContactSummary } from '@/lib/attachments-queries';
import type { AttachmentAnchor } from '@/lib/attachments-queries';
import { usePageTitle } from '@/lib/use-page-title';

export function ReceivablesPage() {
  usePageTitle('Receivables');
  const query = useReceivables();
  const data = query.data;

  // All hooks must be called on every render in the same order. Previously
  // the useMemo / useState / useLastContact below sat AFTER the isLoading
  // early-return, which made hook counts differ between first-render
  // (loading) and second-render (loaded) — React calls that "Rendered more
  // hooks than during the previous render." Keep them up here.
  const projectIds = useMemo(() => {
    if (!data) return [];
    const ids = new Set<string>();
    for (const r of data.bgAwaitingAcceptance) ids.add(r.projectId);
    for (const r of data.awaitingPayment) ids.add(r.projectId);
    for (const r of data.earnestAwaitingRelease) ids.add(r.projectId);
    return [...ids];
  }, [data]);
  const bgIds = useMemo(() => data?.bgAwaitingAcceptance.map((b) => b.bgId) ?? [], [data]);
  const lastContact = useLastContact(projectIds, bgIds);
  const [logForAnchor, setLogForAnchor] = useState<AttachmentAnchor | null>(null);

  // Patch P14 — single text search across all three sections. Matches
  // against project number, project title, and client name. A single
  // global box is cleaner than per-section filters for what's really
  // three views of "client owes us X" — and the data already has
  // clientName embedded on every row.
  //
  // Patch P15 — added a client filter alongside the search. Options are
  // built from the unique set of client names *currently present* in the
  // receivables data — there's no value in showing clients with nothing
  // outstanding, and the rows carry clientName (not clientId) so we
  // match by string equality.
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState<string | null>(null);
  const q = search.trim().toLowerCase();
  const matches = (s: { projectNumber: string; projectTitle: string; clientName: string }) => {
    if (q && !`${s.projectNumber} ${s.projectTitle} ${s.clientName}`.toLowerCase().includes(q)) {
      return false;
    }
    if (filterClient && s.clientName !== filterClient) return false;
    return true;
  };

  const clientOptions = useMemo(() => {
    if (!data) return [];
    const names = new Set<string>();
    for (const r of data.bgAwaitingAcceptance) names.add(r.clientName);
    for (const r of data.awaitingPayment) names.add(r.clientName);
    for (const r of data.earnestAwaitingRelease) names.add(r.clientName);
    return [...names].sort().map((name) => ({ value: name, label: name }));
  }, [data]);

  const filteredBgAwaiting = useMemo(
    () => (data?.bgAwaitingAcceptance ?? []).filter(matches),
    [data, q, filterClient],
  );
  const filteredAwaitingPayment = useMemo(
    () => (data?.awaitingPayment ?? []).filter(matches),
    [data, q, filterClient],
  );
  const filteredEarnest = useMemo(
    () => (data?.earnestAwaitingRelease ?? []).filter(matches),
    [data, q, filterClient],
  );

  if (query.isLoading) {
    return (
      <>
        <PageHeader
          title="Receivables"
          subtitle="Capital and decisions sitting with the client. Sorted by how long they've been waiting."
        />
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-secondary" />
        </div>
      </>
    );
  }

  if (query.error) {
    return (
      <>
        <PageHeader title="Receivables" />
        <Card>
          <p className="text-sm text-danger-fg">Failed to load receivables. {query.error.message}</p>
        </Card>
      </>
    );
  }

  if (!data) return null;

  const noneWaiting =
    data.bgAwaitingAcceptance.length === 0 &&
    data.awaitingPayment.length === 0 &&
    data.earnestAwaitingRelease.length === 0;

  return (
    <>
      <PageHeader
        title="Receivables"
        subtitle="Capital and decisions sitting with the client. Sorted by how long they've been waiting."
        actions={
          <ExportCsvButton href="/api/exports/receivables" fallbackFilename="receivables.csv" />
        }
      />

      {/* Top stat — BG capital exposure */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile
          label="Active BG exposure"
          value={
            data.aggregate.activeBgExposurePaisa > 0
              ? formatCurrency(data.aggregate.activeBgExposurePaisa, { compact: true })
              : '—'
          }
          hint="Sum of ACTIVE + ACCEPTED BGs"
        />
        <StatTile
          label="Awaiting payment"
          value={data.awaitingPayment.length.toString()}
          hint={
            data.awaitingPayment.length === 1 ? 'project with invoice in' : 'projects with invoice in'
          }
          tone={data.awaitingPayment.length > 0 ? 'warning' : 'neutral'}
        />
        <StatTile
          label="BGs awaiting acceptance"
          value={data.bgAwaitingAcceptance.length.toString()}
          hint={
            data.bgAwaitingAcceptance.length === 1
              ? 'BG with client'
              : 'BGs with client'
          }
        />
      </div>

      <Card className="mb-3 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex w-72 items-center">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tertiary"
              strokeWidth={1.5}
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search project, title, or client…"
              className="pl-7"
            />
          </div>
          <SearchableSelect
            items={clientOptions}
            value={filterClient}
            onChange={setFilterClient}
            placeholder="Any client"
            clearable
            className="min-w-[200px]"
          />
          {q || filterClient ? (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setFilterClient(null);
              }}
              className="inline-flex items-center gap-1 text-xs text-secondary hover:text-primary"
            >
              <X className="h-3 w-3" strokeWidth={1.5} />
              Clear
            </button>
          ) : null}
        </div>
      </Card>

      {noneWaiting ? (
        <Card>
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" strokeWidth={1.5} />}
            title="Nothing waiting on the client"
            body="No BGs in submission, no invoices outstanding, no earnest money unreturned."
          />
        </Card>
      ) : null}

      {/* BG awaiting acceptance */}
      {filteredBgAwaiting.length > 0 ? (
        <Section
          icon={<Banknote className="h-4 w-4" strokeWidth={1.5} />}
          title="BG awaiting acceptance"
          subtitle="Submitted to the client; not yet signed off. Chase the client."
        >
          <ul className="divide-y divide-border-default">
            {filteredBgAwaiting.map((bg) => (
              <li key={bg.bgId}>
                <ProjectRow
                  projectId={bg.projectId}
                  projectNumber={bg.projectNumber}
                  projectTitle={bg.projectTitle}
                  clientName={bg.clientName}
                  primaryAmount={formatCurrency(bg.amountPaisa, { compact: true })}
                  primaryLabel="BG amount"
                  ageLabel={`Submitted ${formatDate(bg.submittedDate)}`}
                  daysWaiting={bg.daysSinceSubmitted}
                  lastContact={
                    lastContact.data?.byBg[bg.bgId] ??
                    lastContact.data?.byProject[bg.projectId] ??
                    null
                  }
                  lastContactLoading={lastContact.isLoading}
                  onLogFollowUp={() =>
                    setLogForAnchor({
                      projectId: bg.projectId,
                      projectItemId: null,
                      orderBatchId: null,
                      bankGuaranteeId: bg.bgId,
                      inspectionEventId: null,
                      clientId: null,
                      supplierId: null,
                      masterDepartmentId: null,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {/* Awaiting payment */}
      {filteredAwaitingPayment.length > 0 ? (
        <Section
          icon={<ReceiptText className="h-4 w-4" strokeWidth={1.5} />}
          title="Awaiting payment"
          subtitle="Invoice in, cheque not cleared. Largest-aged at the top."
        >
          <ul className="divide-y divide-border-default">
            {filteredAwaitingPayment.map((p) => (
              <li key={p.projectId}>
                <ProjectRow
                  projectId={p.projectId}
                  projectNumber={p.projectNumber}
                  projectTitle={p.projectTitle}
                  clientName={p.clientName}
                  primaryAmount={`${p.partsAwaitingPayment} part${p.partsAwaitingPayment === 1 ? '' : 's'}`}
                  primaryLabel={
                    p.partsChequeReleased > 0 && p.partsChequeNotReleased > 0
                      ? `${p.partsChequeReleased} cheque released · ${p.partsChequeNotReleased} not yet`
                      : p.partsChequeReleased > 0
                        ? 'Cheque released, awaiting clearance'
                        : 'Cheque not yet released'
                  }
                  ageLabel={`Oldest invoice ${formatDate(p.oldestInvoiceDate)}`}
                  daysWaiting={p.daysSinceOldestInvoice}
                  lastContact={lastContact.data?.byProject[p.projectId] ?? null}
                  lastContactLoading={lastContact.isLoading}
                  onLogFollowUp={() =>
                    setLogForAnchor({
                      projectId: p.projectId,
                      projectItemId: null,
                      orderBatchId: null,
                      bankGuaranteeId: null,
                      inspectionEventId: null,
                      clientId: null,
                      supplierId: null,
                      masterDepartmentId: null,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {/* Earnest awaiting release */}
      {filteredEarnest.length > 0 ? (
        <Section
          icon={<Coins className="h-4 w-4" strokeWidth={1.5} />}
          title="Earnest money awaiting release"
          subtitle="Earnest deposited with the client; not yet returned."
        >
          <ul className="divide-y divide-border-default">
            {filteredEarnest.map((e) => (
              <li key={e.projectId}>
                <ProjectRow
                  projectId={e.projectId}
                  projectNumber={e.projectNumber}
                  projectTitle={e.projectTitle}
                  clientName={e.clientName}
                  primaryAmount={
                    e.earnestAmountPaisa != null
                      ? formatCurrency(e.earnestAmountPaisa, { compact: true })
                      : '—'
                  }
                  primaryLabel="Earnest"
                  ageLabel={`Submitted ${formatDate(e.earnestSubmittedDate)}`}
                  daysWaiting={e.daysSinceSubmitted}
                  lastContact={lastContact.data?.byProject[e.projectId] ?? null}
                  lastContactLoading={lastContact.isLoading}
                  onLogFollowUp={() =>
                    setLogForAnchor({
                      projectId: e.projectId,
                      projectItemId: null,
                      orderBatchId: null,
                      bankGuaranteeId: null,
                      inspectionEventId: null,
                      clientId: null,
                      supplierId: null,
                      masterDepartmentId: null,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {logForAnchor ? (
        <LogCommunicationModal
          open={true}
          onClose={() => setLogForAnchor(null)}
          projectId={logForAnchor.projectId ?? null}
          defaultAnchor={logForAnchor}
          defaultDirection="OUTBOUND"
        />
      ) : null}
    </>
  );
}

function Section({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <Card title={title} bodyClassName="p-0" className="mb-6">
      <div className="flex items-center gap-2 border-b border-border-default bg-base/40 px-4 py-2 text-xs text-secondary">
        {icon}
        <span>{subtitle}</span>
      </div>
      {children}
    </Card>
  );
}

function ProjectRow({
  projectId,
  projectNumber,
  projectTitle,
  clientName,
  primaryAmount,
  primaryLabel,
  ageLabel,
  daysWaiting,
  lastContact,
  lastContactLoading,
  onLogFollowUp,
}: {
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  clientName: string;
  primaryAmount: string;
  primaryLabel: string;
  ageLabel: string;
  daysWaiting: number;
  /** Patch 2 — most-recent comm for this row (project or BG-specific). */
  lastContact?: LastContactSummary | null;
  /** Patch 3 — whether the last-contact lookup is still in flight. */
  lastContactLoading?: boolean;
  /** Patch 2 — opens the log-comm modal with this row's anchor pre-filled. */
  onLogFollowUp?: () => void;
}) {
  // Tone the days-waiting badge stronger past 30 days — that's "this needs
  // a phone call" territory.
  const isStale = daysWaiting >= 30;
  return (
    <div className="flex flex-col gap-1 px-4 py-3 hover:bg-subtle">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/projects/$projectId"
          params={{ projectId }}
          className="min-w-0 flex-1"
        >
          <div className="flex items-center gap-2">
            <ProjectNumber number={projectNumber} size="sm" />
            <span className="truncate font-medium text-primary">{projectTitle}</span>
          </div>
          <div className="mt-0.5 truncate text-xs text-secondary">{clientName} · {ageLabel}</div>
        </Link>
        <div className="flex shrink-0 flex-col items-end">
          <div className="text-sm font-medium tabular-nums text-primary">{primaryAmount}</div>
          <div className="text-[11px] text-secondary">{primaryLabel}</div>
        </div>
        <div
          className={
            isStale
              ? 'shrink-0 rounded-sm bg-warning-strong/10 px-2 py-0.5 text-xs font-medium tabular-nums text-warning-strong'
              : 'shrink-0 text-xs tabular-nums text-secondary'
          }
        >
          {isStale ? (
            <span className="inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />
              {daysWaiting}d
            </span>
          ) : (
            `${daysWaiting}d`
          )}
        </div>
      </div>

      {/* Patch 2 — last contact line + Log follow-up affordance */}
      {(lastContact || onLogFollowUp || lastContactLoading) ? (
        <div className="flex items-center gap-2 pl-1 text-[11px] text-secondary">
          {lastContactLoading ? (
            <span
              aria-hidden
              className="inline-block h-3 w-32 animate-pulse rounded-sm bg-subtle"
            />
          ) : lastContact ? (
            <span title={lastContact.subject}>
              Last contact:{' '}
              <span className="text-primary">
                {formatRelative(lastContact.occurredAt)}
              </span>
              {' — '}
              {COMM_TYPE_SHORT[lastContact.commType]}
              {lastContact.counterpartyName
                ? ` (${lastContact.counterpartyName})`
                : ''}
            </span>
          ) : (
            <span className="italic text-tertiary">No contact logged.</span>
          )}
          {onLogFollowUp && !lastContactLoading ? (
            <button
              type="button"
              onClick={onLogFollowUp}
              className="ml-auto inline-flex items-center gap-1 rounded-sm border border-border-default px-1.5 py-0.5 text-[10px] text-secondary hover:border-accent hover:text-accent-strong"
            >
              <MessageCircleReply className="h-3 w-3" strokeWidth={1.5} />
              {lastContact ? 'Log follow-up' : 'Log first follow-up'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const COMM_TYPE_SHORT: Record<string, string> = {
  PHONE_CALL: 'Phone call',
  MEETING: 'Meeting',
  LETTER_RECEIVED: 'Letter in',
  LETTER_SENT: 'Letter out',
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  INTERNAL_NOTE: 'Note',
};
